import { Icon } from "@progress/kendo-react-common";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { Field, FormRenderProps } from "@progress/kendo-react-form";
import { Error } from "@progress/kendo-react-labels";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import configApi from "../../../api/configuration";
import contractsApi from "../../../api/contracts";
import products from "../../../api/products";
import quotesApi from "../../../api/quotes";
import { getStructures } from "../../../api/vanilla";
import { useApi, useFetch } from "../../../hooks/useApi";
import DateUtility from "../../../utilities/dateUtilities";
import { requiredDateRangePickerValidator, requiredValidator } from "../../../validators";
import { LoadingIndicator } from "../../LoadingIndicator";
import { FormDateRangePicker, FormInput } from "../../form";
import { FormChipList } from "../../form/FormChipList";

type Props = {
  formRenderProps: FormRenderProps;
  product: any;
  isDealEntry?: boolean;
  readOnly?: boolean;
  onLoaded?: () => void;
};

const StartVanillaQuote = ({ formRenderProps, product, isDealEntry, readOnly, onLoaded }: Props) => {
  const { commodity, instrument, company } = product;
  const { calendarMonthOffset } = instrument;
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);

  const getContractDatesApi = useApi(contractsApi.getDateBoundaries);
  const getConfigurationApi = useApi(configApi.getConfiguration);

  const getSettlementTypes = useQuery({
    queryKey: ["settlementTypes", commodity.id, instrument.id],
    queryFn: () => products.getSettlementTypes(commodity.id, instrument.id),
  });

  const quoteId = formRenderProps.valueGetter("quoteId");

  const queryClient = useQueryClient();
  const renameQuoteMutation = useMutation({
    mutationFn: (data: { quoteId: number; name: string }) =>
      quotesApi.renameQuote(data.quoteId, data.name),
    onSuccess: () => {
      if (quoteId) {
        queryClient.invalidateQueries({ queryKey: ["quote", quoteId] });
        queryClient.invalidateQueries({ queryKey: ["quotes"] });
      }
    },
  });
  const { data: structuresData, loading: structuresLoading } = useFetch(
    getStructures,
    instrument.id,
  );
  const structures = useMemo(
    () =>
      structuresData?.structures
        .filter((structure: any) => structure.isActive)
        .map((structure: any) => {
          return {
            text: structure.longName,
            value: structure.structureId,
          };
        }) || [],
    [structuresData?.structures],
  );

  const settlementTypesObject = getSettlementTypes.data?.settlementTypes || {};
  const settlementTypes = Object.keys(settlementTypesObject).map((id) => {
    return {
      text: settlementTypesObject[id],
      value: parseInt(id),
    };
  });

  useEffect(() => {
    getConfigurationApi.request(company.id, commodity.id, instrument.id, instrument.productType);
    onLoaded && onLoaded();
  }, []);

  useEffect(() => {
    const data = getContractDatesApi.data;
    if (data) {
      const { minDate, maxDate } = data;
      let newMinDate = null,
        newMaxDate = null;
      if (minDate) {
        newMinDate = DateUtility.convertToDateWithOffset(data.minDate, -1 * calendarMonthOffset);
      }
      if (maxDate) {
        newMaxDate = DateUtility.convertToDateWithOffset(data.maxDate, -1 * calendarMonthOffset);
      }
      setMinDate(newMinDate);
      setMaxDate(newMaxDate);
    }
  }, [getContractDatesApi.data]);

  useEffect(() => {
    const configId = getConfigurationApi.data?.configurationId || 0;
    formRenderProps.onChange("configurationId", { value: configId });
    getContractDatesApi.request(
      commodity?.abbreviation,
      configId,
      instrument?.name?.toLowerCase() === "swap",
    );
  }, [getConfigurationApi.data]);

  useEffect(() => {
    const loading = getContractDatesApi.loading || getConfigurationApi.loading || structuresLoading;
    formRenderProps.onChange("loaded", { value: !loading });
  }, [getContractDatesApi.loading, getConfigurationApi.loading, structuresLoading]);

  useEffect(() => {
    const isStructureFetched = structures.length > 0;
    const currentStructure = formRenderProps.valueGetter("structureId");
    const hasValidCurrentStructure = settlementTypes.some(
      (settlement) => settlement.value === currentStructure,
    );
    if ((isDealEntry && isStructureFetched && !hasValidCurrentStructure) || structures.length === 1) {
      formRenderProps.onChange("structureId", { value: structures[0].value });
    }
  }, [structures]);

  useEffect(() => {
    const isSettlementFetched = settlementTypes.length > 0;
    const currentSettlement = formRenderProps.valueGetter("settlement");
    const hasValidCurrentSettlement = settlementTypes.some(
      (settlement) => settlement.value === currentSettlement,
    );
    if (isSettlementFetched && !hasValidCurrentSettlement) {
      formRenderProps.onChange("settlement", { value: settlementTypes[0].value });
    }
  }, [settlementTypes.length]);

  const validateDateRange = (value: SelectionRange) => {
    const validationMessage = requiredDateRangePickerValidator(value);
    if (validationMessage) return validationMessage;

    if (
      (minDate && value.start && value.start < minDate) ||
      (maxDate && value.end && value.end > maxDate)
    ) {
      return `Please select dates between ${DateUtility.formatDateMY(
        minDate || "",
      )} and ${DateUtility.formatDateMY(maxDate || "")}`;
    }
  };

  useEffect(() => {
    const originalAllocationId = formRenderProps.valueGetter("originalAllocationId");
    formRenderProps.onChange("allocationId", { value: originalAllocationId });
  }, []);

  const isSwap = formRenderProps.valueGetter("structureId") === 17;
  useEffect(() => {
    if (isSwap) {
      formRenderProps.onChange("commonStrike", { value: false });
    }
  }, [isSwap]);

  const dateLabel = calendarMonthOffset === 0 ? "Contract" : "Calendar";

  const handleBlurQuoteName = (event: React.FocusEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (quoteId && value) {
      const data = { quoteId, name: value };
      renameQuoteMutation.mutate(data);
    }
  };

  const handleChangeQuoteName = () => {
    renameQuoteMutation.reset();
  };
  return (
    <div className="stepper-form" style={{ display: "flex", justifyContent: "center" }}>
      <div style={{ width: "60%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ flexBasis: "90%" }}>
            <Field
              name={"quoteName"}
              label={"Quote Name"}
              component={FormInput}
              validator={requiredValidator}
              onBlurOverride={handleBlurQuoteName}
              onChangeOverride={handleChangeQuoteName}
              disabled={readOnly}
            />
          </div>
          <div
            style={{
              flexBasis: "10%",
              display: "flex",
              alignItems: "flex-end",
              marginTop: "auto",
              marginBottom: "5px",
              marginLeft: "5px",
            }}
          >
            {renameQuoteMutation.isSuccess && (
              <div>
                <Icon name="check" style={{ color: "green" }} size="large" />
              </div>
            )}
            <LoadingIndicator loading={renameQuoteMutation.isPending} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <LoadingIndicator loading={getContractDatesApi.loading} />
          {getContractDatesApi.error && <Error>{getContractDatesApi.error}</Error>}
          {!getContractDatesApi.loading && minDate && maxDate && (
            <Field
              name={"contract"}
              label={dateLabel}
              component={FormDateRangePicker}
              startDateInputSettings={{ label: "" }}
              endDateInputSettings={{ label: "" }}
              calendarSettings={{ bottomView: "year", topView: "decade" }}
              min={minDate}
              max={maxDate}
              format={"MMM yyyy"}
              validator={validateDateRange}
              disabled={readOnly}
            />
          )}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
          }}
        >
          <Field
            name="structureId"
            label="Product"
            component={FormChipList}
            data={structures}
            selection="single"
            validator={requiredValidator}
            disabled={readOnly}
          />
          <LoadingIndicator loading={getSettlementTypes.isPending} />
          {getSettlementTypes.isError && <Error>{getSettlementTypes.error}</Error>}
          <Field
            name="settlement"
            label="Settlement"
            component={FormChipList}
            selection="single"
            data={settlementTypes}
            validator={requiredValidator}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
};

export default StartVanillaQuote;
